import AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';
import {
	envType,
	originType,
	userType,
} from '@atlassiansox/analytics-web-client/analytics-web-types';
import type { AnalyticsProps } from './types';

const ANALYTICS_PRODUCT = 'embeddedConfluence';
const defaultOrgId = '';
const orgIdQuery = `query OrgId($hostNames: [String!]) {
    tenantContexts(hostNames: $hostNames) {
      orgId
    }
  }`;

const getAGGOrgId = async (hostname?: string): Promise<string> => {
	let orgId = defaultOrgId; // default value set to empty string for orgid when no orgid exists
	const prefix = `${window.location.protocol}//${hostname || window.location.hostname}`;

	try {
		const response = await fetch(`${prefix}/gateway/api/graphql`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				query: orgIdQuery,
				variables: {
					hostNames: [hostname],
				},
			}),
		});

		const result = await response.json();
		orgId = result.data?.tenantContexts[0]?.orgId ?? defaultOrgId;
	} catch {
		// eslint-disable-next-line no-console
		console.error(`Error fetching orgId from ${hostname}/gateway/api/graphql`);
	}
	return orgId;
};

// Define default analytic-web-client environment config for now
const getAnalyticsEnv = (hostname: string) => {
	if (hostname.includes('localhost')) {
		return envType.LOCAL;
	}
	if (hostname.includes('jira-dev')) {
		return envType.DEV;
	}

	return envType.PROD;
};

let analyticsClient: AnalyticsWebClient;

const getUserInfo = async (
	hostname: AnalyticsProps['hostname'],
	userInfo: AnalyticsProps['userInfo'],
) => {
	if (userInfo) {
		return { userId: userInfo?.userId, userIdType: userInfo?.userIdType };
	} else {
		const prefix = `${window.location.protocol}//${hostname || window.location.hostname}`;
		const res = await fetch(`${prefix}/gateway/api/me`);
		const resJson = await res.json();
		const { account_id, account_type } = resJson;
		return account_type === 'atlassian'
			? { userId: account_id, userIdType: userType.ATLASSIAN_ACCOUNT }
			: undefined;
	}
};

export const getAnalyticsWebClient = async ({
	hostname,
	userInfo,
}: {
	hostname?: string;
	userInfo: AnalyticsProps['userInfo'];
}): Promise<AnalyticsWebClient> => {
	if (!analyticsClient) {
		analyticsClient = new AnalyticsWebClient({
			product: ANALYTICS_PRODUCT,
			env: getAnalyticsEnv(hostname || window.location.hostname),
			origin: originType.WEB,
		});

		try {
			const orgId = await getAGGOrgId(hostname);
			analyticsClient.setOrgInfo(orgId);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error); // orgId is not set when undefined or empty string based on current impl of setOrgInfo
		}

		try {
			const fetchedUserInfo = await getUserInfo(hostname, userInfo);
			if (fetchedUserInfo) {
				analyticsClient.setUserInfo(fetchedUserInfo.userIdType, fetchedUserInfo.userId);
			}
		} catch {
			// eslint-disable-next-line no-console
			console.error(`Error fetching userInfo from ${hostname}/gateway/api/me`);
		}
	}

	return analyticsClient;
};
